import React, { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { upsertSubject } from '../../Requests/SubjectRequests';
import { useTranslation } from 'react-i18next';

const SubjectCreationModal = ({ handleClose, project, onSave, subject = null }) => {
    const [formData, setFormData] = useState({
        name: subject?.name || '',
        description: subject?.description || '',
        property_values: {}
    });

    const { t } = useTranslation();
    useEffect(() => {
        if (subject?.property_values) {
            const propertyValues = {};
            subject.property_values.forEach(pv => {
                propertyValues[pv.property_definition_id] = {
                    property_definition_id: pv.property_definition_id,
                    value: pv.value
                };
            });
            setFormData(prev => ({
                ...prev,
                property_values: propertyValues
            }));
        }
    }, [subject]);

    const handleInputChange = (propertyId, value) => {
        setFormData(prev => ({
            ...prev,
            property_values: {
                ...prev.property_values,
                [propertyId]: {
                    property_definition_id: propertyId,
                    value: value
                }
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await upsertSubject({
                uuid: subject?.uuid,
                project_id: project.id,
                name: formData.name,
                description: formData.description,
                property_values: Object.values(formData.property_values)
            });
            onSave(response.data);
            handleClose();
        } catch (error) {
            console.error('Error saving subject:', error);
        }
    };

    const renderPropertyInput = (property) => {
        switch (property.type) {
            case 'text':
                return (
                    <input
                        type="text"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                        value={formData.property_values[property.id]?.value || ''}
                        onChange={(e) => handleInputChange(property.id, e.target.value)}
                        required={property.required}
                    />
                );
            case 'number':
                return (
                    <input
                        type="number"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                        value={formData.property_values[property.id]?.value || ''}
                        onChange={(e) => handleInputChange(property.id, parseFloat(e.target.value))}
                        min={property.min_value}
                        max={property.max_value}
                        required={property.required}
                    />
                );
            case 'date':
                return (
                    <input
                        type="date"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                        value={formData.property_values[property.id]?.value || ''}
                        onChange={(e) => handleInputChange(property.id, e.target.value)}
                        required={property.required}
                    />
                );
            case 'boolean':
                return (
                    <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                        checked={formData.property_values[property.id]?.value || false}
                        onChange={(e) => handleInputChange(property.id, e.target.checked)}
                    />
                );
            case 'select':
                return (
                    <select
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                        value={formData.property_values[property.id]?.value || ''}
                        onChange={(e) => handleInputChange(property.id, e.target.value)}
                        required={property.required}
                    >
                        <option value="">Select an option</option>
                        {property.options?.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                );
            case 'multiple_select':
                return (
                    <select
                        multiple
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                        value={formData.property_values[property.id]?.value || []}
                        onChange={(e) => handleInputChange(property.id,
                            Array.from(e.target.selectedOptions, option => option.value))}
                        required={property.required}
                    >
                        {property.options?.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                );
            default:
                return null;
        }
    };

    return (
        <Dialog open={true} onClose={handleClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="bg-white rounded-lg max-w-md w-full p-6">
                    <div className="flex justify-between items-center mb-4">
                        <Dialog.Title className="text-lg font-medium">
                            {subject ? t('SubjectCreationModal.EditSubject') : t('SubjectCreationModal.CreateSubject')}
                        </Dialog.Title>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('SubjectCreationModal.SubjectName')}</label>
                            <input
                                type="text"
                                required
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('SubjectCreationModal.SubjectDescription')}</label>
                            <textarea
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            />
                        </div>

                        {project.property_definitions?.map((property) => (
                            <div key={property.id}>
                                <label className="block text-sm font-medium text-gray-700">
                                    {property.name}
                                    {property.required && <span className="text-red-500">*</span>}
                                </label>
                                <div className="mt-1">
                                    {renderPropertyInput(property)}
                                </div>
                            </div>
                        ))}

                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                {t('SubjectCreationModal.Cancel')}
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 text-sm font-medium text-white bg-black border border-transparent rounded-md hover:bg-gray-800"
                            >
                                {subject ? t('SubjectCreationModal.SaveSubject') : t('SubjectCreationModal.CreateSubject')}
                            </button>
                        </div>
                    </form>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
};

export default SubjectCreationModal;
